import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { parse, format } from 'date-fns';

import { IOrderCardProps } from './order-card.models';
import { useStyles } from './order-card.styles';
import { Card } from '../../../../common/card';
import { Divider } from '../../../../common/divider';
import { Products } from './products';
import {
  OrderStatus,
  ProductOwnerType,
} from '../../../../../graphql/order/models/get-orders.models';
import { OrderRepeatButton } from './order-repeat-button';
import { getOrderStatusTranslation, getStatusIcon } from '../../../../../utils/order';
import { Icon, IconSizes } from '../../../../common';
import { CustomBreakpoints } from '../../../../../themes/default';

export const OrderCard = ({
  orderNumber,
  orderStatus,
  orderDate,
  orderProducts,
  id,
  onClick,
  storeData,
  rating,
  brandsPoints,
  hasKidProfiles = false,
}: IOrderCardProps): JSX.Element => {
  const classes = useStyles();
  const intl = useIntl();
  const formattedOrderDate = format(new Date(orderDate), 'dd.MM.yyyy');
  const iconType = useMemo(() =>
    getStatusIcon(orderStatus), [orderStatus]);
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down(CustomBreakpoints.smallMobileDown));
  const isLandscapeMobile = useMediaQuery(theme.breakpoints.between(
    CustomBreakpoints.landscapeMobileStart, CustomBreakpoints.landscapeMobileEnd,
  ));
  const containsRestrictedBrandOrAOH24 = useMemo(() =>
    orderProducts.some(product =>
      brandsPoints?.some(brand => (brand.brandId === product.brand && brand.hiddenForC2C) || (product.brand === 'AOH' && product.packSize === 24)),
    ),
  [orderProducts, brandsPoints]);
  const isDeletedKids = useMemo(() => orderProducts.some(
    product => product?.productOwner?.type === ProductOwnerType.dependent && !product?.productOwner?.profile,
  ), [orderProducts]);
  const isShowRepeat = orderStatus === OrderStatus.fulfilled
    && !isDeletedKids
    && !containsRestrictedBrandOrAOH24
    && (!isSmallMobile && !isLandscapeMobile)
    && !storeData?.disabled;

  const isShowRepeatSmall = orderStatus === OrderStatus.fulfilled
    && !isDeletedKids
    && !containsRestrictedBrandOrAOH24
    && (isSmallMobile || isLandscapeMobile)
    && !storeData?.disabled;

  return (
    <Card
      className={classes.root}
      data-testid="order-card"
      id={id}
      onClick={onClick}
    >
      <div className={classes.titleBar}>
        <h3
          className={classes.title}
          data-testid="order-title"
        >
          {intl.formatMessage(
            { id: 'order.card.title' },
            { orderNumber },
          )}
        </h3>
        {isShowRepeat && (
          <div
            data-testid="order-repeat-wrapper"
            className={classes.repeatWrapper}
          >
            <OrderRepeatButton
              orderNumber={orderNumber}
              storeData={storeData}
              orderProducts={orderProducts}
              rating={rating!}
            />
          </div>
        )}
      </div>
      <p
        className={classes.date}
        data-testid="order-date"
      >
        {orderDate && formattedOrderDate}
      </p>
      {isShowRepeatSmall && (
        <div
          data-testid="order-repeat-wrapper-small"
          className={classes.repeatWrapperSmall}
        >
          <OrderRepeatButton
            orderNumber={orderNumber}
            storeData={storeData}
            orderProducts={orderProducts}
            rating={rating!}
          />
        </div>
      )}
      <Products
        products={orderProducts}
        hasKidProfiles={hasKidProfiles}
      />
      <Divider />
      <p
        className={classes.status}
        data-testid="order-status"
      >
        <span
          className={classes.statusIcon}
          data-testid="order-status-icon"
          aria-hidden={true}
        >
          <Icon
            type={iconType}
            size={IconSizes.xxs}
          />
        </span>
        <span
          className={classes.statusNote}
          data-testid="order-status-note"
        >
          <FormattedMessage id={getOrderStatusTranslation(orderStatus)} />
          {orderStatus === 'FULFILLED' && orderDate && (
              <>
                {' '}
                {format(parse(orderDate, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')}
              </>
          )}
        </span>
      </p>
    </Card>
  );
};
